import { useEffect, useState } from "react";
import { Accordion, Button, Form, InputGroup, Offcanvas } from "react-bootstrap";
import DatePicker from 'react-datepicker';
import SimpleBar from "simplebar-react";
import SuccessModal from "../../common/successModal";
import arrayUtilities from "../../../utilities/arrayUtilities";
import dateUtilities from "../../../utilities/dateUtilities";
import enums from "../../../utilities/enums";
import sketchUtilities from "../../../utilities/sketchUtilities";
import stringUtilities from "../../../utilities/stringUtilities";
import useApi from "../../../utilities/useApi";
import OntarioLocateReportAuxiliaryPageFields from "./ontarioLocateReportAuxiliaryPageFields";

const emptyConfiguration = {
    subNumber: '',
    locatingFor: '',
    excavationDate: null,
    revisedExcavationDate: null,
    requestType: '',
    requestedBy: '',
    company: '',
    phone: '',
    email: '',
    appointmentDate: null,
    receivedDate: null,
    contractorType: '',
    typeOfWork: '',
    address: '',
    city: '',
    depth: '',
    callersRemarks: '',
    telecomUtility: 0,
    gasUtility: 0,
    hydroUtility: 0,
    streetLightingUtility: 0,
    waterUtility: 0,
    sanitarySewerUtility: 0,
    stormSewerUtility: 0,
    otherUtility: 0,
    commentsToExcavator: '',
    paint: false,
    stakes: false,
    other: false,
    locatedBy: '',
    idNumber: '',
    completionDate: null,
    acceptedBy: '',
    markAndEmail: false,
    leftOnSite: false,
    putInMailbox: false
};

const utilityFields = [
    { label: "Telecom Utility", field: "telecomUtility" },
    { label: "Gas Utility", field: "gasUtility" },
    { label: "Hydro Utility", field: "hydroUtility" },
    { label: "Street Lighting Utility", field: "streetLightingUtility" },
    { label: "Water Utility", field: "waterUtility" },
    { label: "Sanitary Sewer Utility", field: "sanitarySewerUtility" },
    { label: "Storm Sewer Utility", field: "stormSewerUtility" },
    { label: "Other Utility", field: "otherUtility" }
];

export default function DownloadOntarioLocateReport(props) {
    const { api } = useApi();
    const limits = props.sketch?.limits ?? [];

    const [ticketNumber, setTicketNumber] = useState('');
    const [configuration, setConfiguration] = useState(emptyConfiguration);
    const [auxiliaryPages, setAuxiliaryPages] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [previousReports, setPreviousReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');

    let pointsLinesIsDefault = arrayUtilities.isNullOrEmpty(props.workflowPoints) && arrayUtilities.isNullOrEmpty(props.workflowLines) && arrayUtilities.isNullOrEmpty(props.workflowPointsLines);

    let dataWorkflowIds = !arrayUtilities.isNullOrEmpty(props.points) ? arrayUtilities.distinct(props.points.map(x => x.workflowId)) : [];
    let sketchWorkflowIds = !arrayUtilities.isNullOrEmpty(props.sketch?.items) ? arrayUtilities.distinct(props.sketch.items.map(x => x.tool?.properties?.workflowId)) : [];
    let textMarkerWorkflowIds = !arrayUtilities.isNullOrEmpty(props.sketch?.textMarkers) ? arrayUtilities.distinct(props.sketch.textMarkers.map(x => x.workflowId)) : [];

    let combinedWorkflowIds = arrayUtilities.distinct(dataWorkflowIds.concat(sketchWorkflowIds).concat(textMarkerWorkflowIds)).filter(x => !stringUtilities.isNullOrEmpty(x));

    const workflows = (props.workflows ?? []).filter(workflow =>
        combinedWorkflowIds.includes(workflow.id) &&
        (pointsLinesIsDefault || props.workflowPoints.includes(workflow.id) || props.workflowLines.includes(workflow.id) || props.workflowPointsLines.includes(workflow.id))
    );

    useEffect(() => {
        setAuxiliaryPages([]);
    }, [props.sketch?.id]);

    useEffect(() => {
        api.searchOntarioLocateReports(props.organisationId, props.sketchId, setPreviousReports, () => { });
    }, []);

    const getLimitName = (limitIndex) => sketchUtilities.getLimitName(limits, limitIndex);

    const getAuxiliaryPage = (limitIndex, workflowId) => auxiliaryPages.find(page => page.limitIndex === limitIndex && page.workflowId === workflowId);

    const toggleAuxiliaryPage = (limitIndex, workflowId) => {
        if (getAuxiliaryPage(limitIndex, workflowId)) {
            setAuxiliaryPages(auxiliaryPages.filter(page => !(page.limitIndex === limitIndex && page.workflowId === workflowId)));
        } else {
            setAuxiliaryPages([...auxiliaryPages, { limitId: limits[limitIndex]?.id ?? null, limitIndex, workflowId, locateAreaHasBeenAltered: false }]);
        }
    };

    const updateAuxiliaryPage = (updatedPage) => {
        setAuxiliaryPages(auxiliaryPages.map(page => page.limitIndex === updatedPage.limitIndex && page.workflowId === updatedPage.workflowId ? updatedPage : page));
    };

    const updateConfiguration = (field, value) => {
        setConfiguration(prevState => ({ ...prevState, [field]: value }));
    };

    // Pages in the order they appear in the form (limit, then workflow)
    const orderedAuxiliaryPages = limits.flatMap((_, limitIndex) => workflows.map(wf => getAuxiliaryPage(limitIndex, wf.id)).filter(page => page !== undefined));

    const canSubmit = !stringUtilities.isNullOrEmpty(configuration.excavationDate)
        && !stringUtilities.isNullOrEmpty(configuration.receivedDate)
        && !stringUtilities.isNullOrEmpty(configuration.completionDate)
        && !arrayUtilities.isNullOrEmpty(orderedAuxiliaryPages);

    const onSelectPreviousReport = (ontarioLocateReportTaskId) => {
        setSelectedReportId(ontarioLocateReportTaskId);

        if (!ontarioLocateReportTaskId)
        {
            setTicketNumber('');
            setConfiguration(emptyConfiguration);
            setAuxiliaryPages([]);
            return;
        }

        const selectedReport = previousReports.find(report => report.ontarioLocateReportTaskId === ontarioLocateReportTaskId);
        if (!selectedReport)
        {
            return;
        }

        const { auxiliaryPages: previousAuxiliaryPages, ...previousConfiguration } = selectedReport.configuration ?? {};

        setTicketNumber(selectedReport.ticketNumber ?? '');
        setConfiguration({ ...emptyConfiguration, ...previousConfiguration });
        setAuxiliaryPages((previousAuxiliaryPages ?? []).filter(page => page.limitIndex < limits.length && workflows.some(wf => wf.id === page.workflowId)));
    };

    const onSubmit = () => {
        if (!canSubmit)
        {
            return;
        }

        setIsSubmitting(true);
        const selections = limits.reduce((acc, limit, limitIndex) => {
            const workflowIds = orderedAuxiliaryPages.filter(page => page.limitIndex === limitIndex).map(page => page.workflowId);
            if (workflowIds.length) {
                acc.push({ limitId: limit?.id ?? null, limitIndex, workflowIds, includeAll: false });
            }
            return acc;
        }, []);

        api.requestOntarioLocateReport(
            props.organisationId, props.projectId, props.sketchId,
            ticketNumber, { ...configuration, auxiliaryPages: orderedAuxiliaryPages },
            props.basemap, selections, props.includeDeleted, props.selectedSurfaceIds,
            () => { setIsSubmitting(false); setShowSuccess(true); },
            () => setIsSubmitting(false)
        );
    };

    const onClose = () => {
        if (props.onComplete) {
            props.onComplete();
        }
    };

    const textField = (label, field, type = 'text') =>
        <Form.Group className="mb-3">
            <Form.Label className='text-body fs-sm fw-semibold'>{label}</Form.Label>
            <Form.Control size='sm' type={type} value={configuration[field] ?? ''} onChange={e => updateConfiguration(field, e.target.value)} />
        </Form.Group>;

    const textAreaField = (label, field) =>
        <Form.Group className="mb-3">
            <Form.Label className='text-body fs-sm fw-semibold'>{label}</Form.Label>
            <Form.Control size='sm' as='textarea' rows='3' value={configuration[field] ?? ''} onChange={e => updateConfiguration(field, e.target.value)} />
        </Form.Group>;

    const dateField = (label, field) =>
        <Form.Group className="mb-3">
            <Form.Label className='text-body fs-sm fw-semibold'>{label}</Form.Label>
            <div>
                <Form.Control
                    as={DatePicker}
                    selected={configuration[field] ? new Date(configuration[field]) : null}
                    onChange={date => updateConfiguration(field, date ? date.toISOString() : null)}
                    dateFormat="dd/MM/yyyy"
                    className='rounded-start form-control-sm'
                />
            </div>
        </Form.Group>;

    const switchField = (label, field) =>
        <div className='d-flex align-items-center mb-3'>
            <Form.Check type='switch' id={`olr-${field}`} checked={configuration[field] ?? false} onChange={e => updateConfiguration(field, e.target.checked)} className="me-2" />
            <p className="mb-0 fs-sm">{label}</p>
        </div>;

    return (
        <>
            {showSuccess && (
                <SuccessModal
                    success={{ message: 'Your Ontario Locate Report is being generated. You will receive an email when it is ready to download.' }}
                    onDismiss={onClose}
                />
            )}
            <Offcanvas show placement='end' scroll={false} backdrop={false} id="ontario-locate-report-dialog">
                <Offcanvas.Header className='border-bottom'>
                    <Offcanvas.Title>Download Ontario Locate Report</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="d-flex flex-align-start flex-column h-100 w-100">
                        <div className="w-100 h-100 mb-auto overflow-y-auto">
                            <SimpleBar autoHide={false} className='simplebar-no-autohide pe-3' style={{ maxHeight: 'calc(100vh - 200px)' }}>
                                {previousReports.length > 0 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className='text-body fs-sm fw-semibold'>Load from Previous Report</Form.Label>
                                        <InputGroup size='sm'>
                                            <Form.Select
                                                value={selectedReportId}
                                                onChange={e => onSelectPreviousReport(e.target.value)}
                                            >
                                                <option value="" hidden></option>
                                                {previousReports.map(report => (
                                                    <option
                                                        key={report.ontarioLocateReportTaskId}
                                                        value={report.ontarioLocateReportTaskId}
                                                    >
                                                        {report.ticketNumber || 'Untitled'} - {dateUtilities.formatToDateString(report.createdAt)} {dateUtilities.formatToTimeString(report.createdAt)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Button variant="outline-secondary" onClick={() => onSelectPreviousReport('')}>
                                                <i className='fi-x'></i>
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>
                                )}

                                <Accordion defaultActiveKey='0'>
                                    <Accordion.Item eventKey='0'>
                                        <Accordion.Header>Section One</Accordion.Header>
                                        <Accordion.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Label className='text-body fs-sm fw-semibold'>Ticket Number</Form.Label>
                                                <Form.Control size='sm' type='number' value={ticketNumber} onChange={e => setTicketNumber(e.target.value)} />
                                            </Form.Group>
                                            {textField('Sub Number', 'subNumber', 'number')}
                                            {textField('Locating For', 'locatingFor')}
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey='1'>
                                        <Accordion.Header>Section Two</Accordion.Header>
                                        <Accordion.Body>
                                            {dateField('Revised Excavation Date', 'revisedExcavationDate')}
                                            {dateField('Excavation Date', 'excavationDate')}
                                            {textField('Request Type', 'requestType')}
                                            {textField('Requested By', 'requestedBy')}
                                            {textField('Company', 'company')}
                                            {textField('Phone', 'phone', 'number')}
                                            {textField('Email', 'email')}
                                            {dateField('Appointment Date', 'appointmentDate')}
                                            {dateField('Received Date', 'receivedDate')}
                                            {textField('Contractor Type', 'contractorType')}
                                            {textField('Type of Work', 'typeOfWork')}
                                            {textField('Address', 'address')}
                                            {textField('City', 'city')}
                                            {textField('Depth', 'depth', 'number')}
                                            {textAreaField('Callers Remarks', 'callersRemarks')}
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey='2'>
                                        <Accordion.Header>Utilities Located</Accordion.Header>
                                        <Accordion.Body>
                                            {utilityFields.map(utility => (
                                                <Form.Group key={utility.field} className="mb-3">
                                                    <Form.Label className='text-body fs-sm fw-semibold'>{utility.label}</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        {Object.entries(enums.LocateReportUtilityType).map(([key, value]) => (
                                                            <Form.Check
                                                                key={key}
                                                                type='radio'
                                                                id={`olr-${utility.field}-${key}`}
                                                                name={`olr-${utility.field}`}
                                                                label={value}
                                                                className='text-body fs-sm'
                                                                checked={Number(key) === configuration[utility.field]}
                                                                onChange={() => updateConfiguration(utility.field, Number(key))}
                                                            />
                                                        ))}
                                                    </div>
                                                </Form.Group>
                                            ))}
                                            {textAreaField('Comments to Excavator', 'commentsToExcavator')}
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey='3'>
                                        <Accordion.Header>Method of Field Markings</Accordion.Header>
                                        <Accordion.Body>
                                            {switchField('Paint', 'paint')}
                                            {switchField('Stakes', 'stakes')}
                                            {switchField('Other', 'other')}
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey='4'>
                                        <Accordion.Header>Form section</Accordion.Header>
                                        <Accordion.Body>
                                            {textField('Located By', 'locatedBy')}
                                            {textField('ID Number', 'idNumber', 'number')}
                                            {dateField('Completion Date', 'completionDate')}
                                            {textField('Accepted By', 'acceptedBy')}
                                            {switchField('Mark and Email', 'markAndEmail')}
                                            {switchField('Left on Site', 'leftOnSite')}
                                            {switchField('Put in Mailbox', 'putInMailbox')}
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey='5'>
                                        <Accordion.Header>Auxiliary Pages</Accordion.Header>
                                        <Accordion.Body>
                                            {limits.map((_, limitIndex) => (
                                                <div key={limitIndex} className="mb-3">
                                                    <p className="fw-semibold mb-2">{getLimitName(limitIndex)}</p>
                                                    {workflows.map(wf => {
                                                        const auxiliaryPage = getAuxiliaryPage(limitIndex, wf.id);
                                                        return (
                                                            <div key={wf.id}>
                                                                <div className='d-flex align-items-center mb-2'>
                                                                    <Form.Check type='switch' id={`olr-page-${limitIndex}-${wf.id}`} checked={auxiliaryPage !== undefined} onChange={() => toggleAuxiliaryPage(limitIndex, wf.id)} className="me-2" />
                                                                    <p className="mb-0">{wf.name || wf.id}</p>
                                                                </div>
                                                                {auxiliaryPage &&
                                                                    <div className="ms-4">
                                                                        <OntarioLocateReportAuxiliaryPageFields auxiliaryPage={auxiliaryPage} onChange={updateAuxiliaryPage} />
                                                                    </div>
                                                                }
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </SimpleBar>
                        </div>
                        <div className="d-flex justify-content-between order-2 w-100">
                            <Button onClick={onClose} variant="secondary" size="sm">Cancel</Button>
                            <Button onClick={onSubmit} variant="primary" size="sm" disabled={isSubmitting || !canSubmit}><i className="fi-download me-2"></i> Download</Button>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}
